import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NavigationCancel, NavigationEnd, NavigationError, NavigationStart,
  Router, RouterLink, RouterLinkActive, RouterOutlet,
} from '@angular/router';
import { CartService } from '../data/cart.service';
import { CustomerAuthService } from '../data/customer-auth.service';
import { LocationService } from '../data/location.service';
import { LocationDriftService } from '../data/location-drift.service';
import { ArrivalAlertService } from '../data/arrival-alert.service';
import { AuthModalService } from '../data/auth-modal.service';
import { LocationModalComponent } from './location-modal/location-modal';
import { AuthModalComponent } from './auth-modal/auth-modal';
import { API_BASE } from '../data/ordering-api';
import { InboxBell } from '../../../Shared/inbox-bell';
import { InstallAppComponent, InstallHelpSheetComponent, InstallPromptComponent } from '../../../Shared/install-app';
import { GetAppBannerComponent } from '../../../Shared/get-app';
import { GetAppModalComponent } from './get-app-modal/get-app-modal';
import { LangSwitcherComponent } from '../../../Shared/i18n/lang-switcher';
import { TextSizeControlComponent } from '../../../Shared/text-size/text-size-control';
import { BottomNavComponent } from '../../../Shared/bottom-nav/bottom-nav';
import { I18nService } from '../../../Shared/i18n/i18n.service';
import { BrandLogoDirective } from '../../../Shared/brand-logo/brand-logo.directive';
import { BikeLoaderComponent } from '../../../Shared/bike-loader/bike-loader';

/**
 * Self-contained shell for the customer ordering portal — its OWN red/black header (logo, location
 * selector, account, cart), a Shops/Services/Bookings tab bar, the location picker modal, and a
 * footer. Deliberately NOT the marketing master layout (App hides that on /ordering), so this can
 * later be lifted to its own subdomain (customer.orderyaa.com). Red/black theme via CSS-variable
 * overrides on the .oy wrapper, which cascade to every routed child page.
 */
@Component({
  selector: 'app-ordering-layout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    LocationModalComponent,
    AuthModalComponent,
    InboxBell,
    InstallAppComponent,
    InstallHelpSheetComponent,
    InstallPromptComponent,
    GetAppBannerComponent,
    GetAppModalComponent,
    LangSwitcherComponent,
    TextSizeControlComponent,
    BottomNavComponent,
    BikeLoaderComponent,
    BrandLogoDirective,
  ],
  templateUrl: './ordering-layout.html',
  styleUrls: ['./ordering-layout.css'],
})
export class OrderingLayoutComponent {
  protected readonly cart = inject(CartService);
  protected readonly auth = inject(CustomerAuthService);
  protected readonly loc = inject(LocationService);
  protected readonly i18n = inject(I18nService);
  private readonly drift = inject(LocationDriftService);
  /** App-wide 'your rider is outside' alarm — see ArrivalAlertService for why it is not page-scoped. */
  protected readonly arrival = inject(ArrivalAlertService);
  protected readonly authModal = inject(AuthModalService);
  private readonly router = inject(Router);
  protected readonly year = new Date().getFullYear();

  /** Customer in-app inbox (Customer scheme) — order lifecycle notifications land here. */
  protected readonly inboxBase = `${API_BASE}/api/notifications/inbox`;

  /**
   * Mobile nav drawer. The header can't hold the location picker, search, the account links, the language
   * picker and the cart on a phone, so on narrow screens the links move in here and the bar keeps only what
   * a shopper needs at a glance: where they're shopping, what they're searching, and their basket.
   */
  readonly menuOpen = signal(false);
  toggleMenu(): void { this.menuOpen.update((v) => !v); }
  closeMenu(): void { this.menuOpen.set(false); }

  /**
   * The delivery bike, riding under the header while a route loads — the same animation as the splash,
   * doing the job a progress bar does. Every page here is lazy-loaded, so tapping a nav link or the
   * logo can mean fetching a chunk, and until now that was silent.
   *
   * Two guards stop it flickering, which is what makes a loader feel broken rather than helpful:
   *  • it waits SHOW_AFTER_MS before appearing, so a cached route that resolves instantly never
   *    flashes a bar at all;
   *  • once shown it stays MIN_SHOWN_MS, so it can't appear and vanish within a frame or two.
   */
  readonly navLoading = signal(false);
  private static readonly SHOW_AFTER_MS = 120;
  private static readonly MIN_SHOWN_MS = 500;
  private navShowTimer: ReturnType<typeof setTimeout> | undefined;
  private navHideTimer: ReturnType<typeof setTimeout> | undefined;
  private navShownAt = 0;

  private startNavProgress(): void {
    clearTimeout(this.navHideTimer);
    if (this.navLoading()) return;                  // already running from a prior navigation
    clearTimeout(this.navShowTimer);
    this.navShowTimer = setTimeout(() => {
      this.navShownAt = performance.now();
      this.navLoading.set(true);
    }, OrderingLayoutComponent.SHOW_AFTER_MS);
  }

  private endNavProgress(): void {
    clearTimeout(this.navShowTimer);
    if (!this.navLoading()) return;                 // it never appeared — nothing to take down
    const shownFor = performance.now() - this.navShownAt;
    const wait = Math.max(0, OrderingLayoutComponent.MIN_SHOWN_MS - shownFor);
    clearTimeout(this.navHideTimer);
    this.navHideTimer = setTimeout(() => this.navLoading.set(false), wait);
  }

  logout(): void {
    this.closeMenu();
    this.auth.logout();
    this.router.navigate(['/discover']);
  }

  /** Sign in / sign up from inside the drawer — close it first so the modal isn't behind the panel. */
  openAuth(mode: 'login' | 'signup'): void {
    this.closeMenu();
    this.authModal.open(null, mode);
  }

  constructor() {
    // Route progress. Cancel and Error matter as much as End: without them a guard redirect or a
    // failed lazy chunk would leave the bar riding forever.
    this.router.events.pipe(takeUntilDestroyed()).subscribe((e) => {
      if (e instanceof NavigationStart) this.startNavProgress();
      else if (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) {
        this.endNavProgress();
      }
    });

    // After a page refresh the token is restored from storage but the profile isn't — load it so the
    // header can greet the user by name (and Account page has data ready). Best-effort; ignored on failure.
    if (this.auth.isAuthenticated() && !this.auth.profile()) {
      this.auth.loadProfile().subscribe({ error: () => {} });
    }

    // Watch for the shopper having physically moved away from the delivery spot they chose. Started
    // from the layout because it must run wherever they are in the app, not on one page — and it only
    // ever acts when location permission is already granted, so this costs a browsing guest nothing.
    this.drift.start();

    // Watch for a rider arriving on ANY page. The tracking screen is where a customer looks LEAST while
    // waiting, so an alarm that only exists there fires at the one moment nobody is watching.
    this.arrival.start();
  }

  /**
   * Show the floating arrival banner — unless the customer is ALREADY on that order's tracking page,
   * where the in-page panel says the same thing. Two of them stacked is noise, and the panel is the
   * richer one.
   */
  get showArrivalBanner(): boolean {
    const o = this.arrival.alert();
    if (!o?.orderId) return false;
    return !this.router.url.startsWith(`/order/${o.orderId}`);
  }

  /** First name for the account nav link, e.g. "Muhammad Ali" → "Muhammad". Falls back to "Account".
   *  A real name is never translated; only the fallback word is. */
  get accountLabel(): string {
    const name = this.auth.profile()?.displayName?.trim();
    return name ? name.split(/\s+/)[0] : this.i18n.t('nav.account');
  }

  /** The page the drawer was opened on, so a feedback report says where the customer was when they wrote it. */
  get feedbackFrom(): string {
    return this.router.url.split('?')[0];
  }
}
